/**
 * Утилиты для преобразования данных строки таблицы в поля для скриптов/UniData.
 * Без хардкода имён полей — рекурсивное сглаживание и опциональная конфигурация fallback.
 */

export interface TypeIdFallbackConfig {
  /** Значения typeId, при которых ищем fallback (напр. summary-document) */
  placeholderValues: string[]
  /** Пути для поиска альтернативного typeId (напр. docType.typeId) */
  fallbackPaths: string[]
}

/** Конфиг по умолчанию: summary-document → docType.typeId */
export const DEFAULT_TYPEID_FALLBACK: TypeIdFallbackConfig = {
  placeholderValues: ['summary-document'],
  fallbackPaths: ['docType.typeId', 'docType._typeId']
}

/** Пропускаемые ключи при flatten (служебные) */
const SKIP_KEYS = new Set(['attributes', 'errors', 'items', '_items', '_originalRow'])

function getNestedValue(obj: any, path: string): any {
  if (!obj || !path) return undefined
  const parts = path.split('.')
  let cur: any = obj
  for (const p of parts) {
    if (cur == null) return undefined
    cur = cur[p] ?? cur?.fields?.[p]?.value ?? cur?.fields?.[p]
  }
  return cur
}

function toTypedValue(val: any): { value: any; type: string } {
  return {
    value: val,
    type:
      typeof val === 'string'
        ? 'String'
        : typeof val === 'number'
          ? 'Long'
          : typeof val === 'boolean'
            ? 'Boolean'
            : 'String'
  }
}

/**
 * Рекурсивно сглаживает объект: { a: { b: 1 } } → { 'a.b': 1 }.
 * Только примитивы. Для полей в формате { value, type } извлекает value.
 */
function flattenToPrimitiveFields(
  obj: any,
  prefix = ''
): Record<string, { value: any; type: string }> {
  const result: Record<string, { value: any; type: string }> = {}

  if (obj == null) return result

  const add = (key: string, val: any) => {
    if (val === null || val === undefined) return
    const raw = val?.value !== undefined ? val.value : val
    if (
      typeof raw === 'string' ||
      typeof raw === 'number' ||
      typeof raw === 'boolean'
    ) {
      result[key] = toTypedValue(raw)
    } else if (typeof raw === 'object' && !Array.isArray(raw)) {
      Object.assign(result, flattenToPrimitiveFields(raw, key + '.'))
    }
  }

  if (obj?.fields && typeof obj.fields === 'object') {
    Object.keys(obj.fields).forEach(k => {
      if (SKIP_KEYS.has(k)) return
      const field = obj.fields[k]
      const val = field?.value !== undefined ? field.value : field
      add(prefix ? `${prefix}${k}` : k, val)
    })
  }

  Object.keys(obj).forEach(k => {
    if (k === 'fields' || SKIP_KEYS.has(k)) return
    const val = obj[k]
    const raw = val?.value !== undefined ? val.value : val
    if (
      typeof raw === 'string' ||
      typeof raw === 'number' ||
      typeof raw === 'boolean'
    ) {
      const key = prefix ? `${prefix}${k}` : k
      if (!(key in result)) result[key] = toTypedValue(raw)
    } else if (val && typeof val === 'object' && !Array.isArray(val)) {
      const nested = flattenToPrimitiveFields(val, prefix ? `${prefix}${k}.` : `${k}.`)
      Object.keys(nested).forEach(nk => {
        if (!(nk in result)) result[nk] = nested[nk]
      })
    }
  })

  return result
}

/**
 * Преобразует строку таблицы в поля для контекста скрипта (onClick и т.д.).
 * Все примитивы извлекаются рекурсивно, без перечисления имён полей.
 *
 * @param rowData - данные строки (selectedData)
 * @param typeIdFallback - опционально: когда подменять typeId (напр. summary-document → docType.typeId)
 */
export function convertRowDataToFieldsForScript(
  rowData: any,
  typeIdFallback?: TypeIdFallbackConfig | null
): Record<string, { value: any; type: string }> {
  if (!rowData || typeof rowData !== 'object') return {}

  const fields = flattenToPrimitiveFields(rowData)

  if (typeIdFallback && typeIdFallback.placeholderValues?.length && typeIdFallback.fallbackPaths?.length) {
    const typeIdVal = (
      fields.typeId?.value ??
      fields.typeId ??
      getNestedValue(rowData, 'typeId')
    )?.toString?.()

    if (typeIdVal && typeIdFallback.placeholderValues.includes(typeIdVal)) {
      for (const path of typeIdFallback.fallbackPaths) {
        const fallbackVal = getNestedValue(rowData, path) ?? fields[path]?.value
        if (
          fallbackVal != null &&
          String(fallbackVal) !== '' &&
          !typeIdFallback.placeholderValues.includes(String(fallbackVal))
        ) {
          fields.typeId = toTypedValue(String(fallbackVal))
          break
        }
      }
    }
  }

  return fields
}

/**
 * Возвращает эффективный typeId для строки: при placeholder (напр. summary-document)
 * подставляет значение из fallback-путей. Используется в createClientDataUniData и др.
 */
export function resolveDocumentTypeId(
  rowData: any,
  config: TypeIdFallbackConfig = DEFAULT_TYPEID_FALLBACK
): string | undefined {
  const typeIdVal = (
    rowData?.typeId ?? rowData?._typeId ?? rowData?.fields?.typeId?.value ?? rowData?.fields?._typeId?.value
  )?.toString?.()
  if (!typeIdVal || !config.placeholderValues.includes(typeIdVal)) {
    return typeIdVal
  }
  for (const path of config.fallbackPaths) {
    const v = getNestedValue(rowData, path)
    if (v != null && String(v) !== '' && !config.placeholderValues.includes(String(v))) {
      return String(v)
    }
  }
  return typeIdVal
}
